import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@parrot-software/design-system';
import { REPORT_PATHS } from '@reports/presentation/utils/reportPaths';

export interface BreadcrumbItemType {
  label: string;
  path?: string;
}

interface ReportBreadcrumbProps {
  items: BreadcrumbItemType[];
  reportPath?: string;
}

/**
 * ReportBreadcrumb - Breadcrumb navigation for reports
 *
 * Displays navigation path with multiple segments.
 * Items with a `path` are clickable links, items without a `path` are displayed as the current page.
 * Automatically adds "Reportes" as the first item if not provided.
 *
 * @param {ReportBreadcrumbProps} props - Component properties
 * @param {BreadcrumbItemType[]} props.items - Array of breadcrumb items
 * @param {string} [props.reportPath] - Path to reports index (default: '/reports')
 * @returns {React.ReactElement} Breadcrumb component
 *
 * @example
 * // Simple usage
 * <ReportBreadcrumb items={[
 *   { label: 'Reporte de Ventas' }
 * ]} />
 *
 * @example
 * // Multiple segments
 * <ReportBreadcrumb items={[
 *   { label: 'Reporte de Ventas', path: '/reports/sales' },
 *   { label: 'Detalle 1', path: '/reports/sales/detail1' },
 *   { label: 'Detalle 2' }
 * ]} />
 */
export const ReportBreadcrumb = ({ items, reportPath = REPORT_PATHS.ROOT }: ReportBreadcrumbProps) => {
  const { t } = useTranslation(['reports', 'reportSales']);

  // Ensure "Reportes" is the first item
  const breadcrumbItems: BreadcrumbItemType[] = [
    { label: 'reportCommon:navigation.reports', path: reportPath },
    ...items,
  ];

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        {breadcrumbItems.flatMap((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return [
            <BreadcrumbItem key={`item-${index}`}>
              {item.path && !isLast ? (
                <BreadcrumbLink asChild>
                  <Link to={item.path}>{t(item.label)}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{t(item.label)}</BreadcrumbPage>
              )}
            </BreadcrumbItem>,
            ...(isLast ? [] : [<BreadcrumbSeparator key={`separator-${index}`} />]),
          ];
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
